import { FC, Fragment, useCallback, useState } from 'react';
import { Flex, Grid, Link, Text } from '@servicetitan/anvil2';

import { MessageCard } from '../../message-card/message-card';
import { MultilineText } from '../../multiline-text/multiline-text';
import { formatDate } from '../../../utils/format-date';
import { InvoiceItemModel } from '../../../models/invoice.model';
import { formatCurrency } from '../../../utils/format-currency';

interface InvoiceDetailProps {
    data: InvoiceItemModel;
}

export const InvoiceDetail: FC<InvoiceDetailProps> = ({ data }) => {
    const [showMore, setShowMore] = useState<boolean | undefined>(undefined);

    const handleLineCalculated = useCallback((isTruncated: boolean) => {
        if (isTruncated) {
            setShowMore(false);
        } else {
            setShowMore(undefined);
        }
    }, []);

    const toggleShowAll = () => {
        setShowMore(!showMore);
    };

    return (
        <Fragment>
            <MessageCard className="m-b-2" flexDirection="column">
                <Text variant="headline" el="h3" size="small">
                    Invoice ID #{data.invoice_id}
                </Text>

                <div className="m-b-1">
                    <Text variant="eyebrow" className="m-b-half" size="small">
                        Invoice Date
                    </Text>
                    <Text size="small">{formatDate(new Date(data.invoice_date))}</Text>
                </div>

                <div className="m-b-1">
                    <Text variant="eyebrow" className="m-b-half" size="small">
                        Invoice Due Date
                    </Text>
                    <Text size="small">{formatDate(new Date(data.invoice_due_date))}</Text>
                </div>

                <div className="m-b-1">
                    <Text variant="eyebrow" className="m-b-half" size="small">
                        Paid Status
                    </Text>

                    {data.paid_status === 'overdue' ? (
                        <Text className="m-b-1 c-red-600" size="small">
                            Overdue
                        </Text>
                    ) : data.paid_status === 'paid' ? (
                        <Text className="m-b-1 c-green-500" size="small">
                            Paid
                        </Text>
                    ) : (
                        <Text className="m-b-1" size="small">
                            {data.paid_status}
                        </Text>
                    )}
                </div>

                <Grid
                    gap="2"
                    templateColumns="1fr 1fr"
                    justifyContent="space-between"
                    alignItems="stretch"
                    className="m-b-1"
                >
                    <Flex direction="column">
                        <Text variant="eyebrow" className="m-b-half" size="small">
                            Invoice Balance
                        </Text>
                        <Text size="small">{formatCurrency(data.invoice_balance)}</Text>
                    </Flex>
                    <Flex direction="column">
                        <Text variant="eyebrow" className="m-b-half" size="small">
                            Invoice Total
                        </Text>
                        <Text size="small">{formatCurrency(data.invoice_total)}</Text>
                    </Flex>
                </Grid>

                <div>
                    <Text variant="eyebrow" className="m-b-half" size="small">
                        Invoice Summary
                    </Text>
                    <MultilineText
                        onLinesCalculated={handleLineCalculated}
                        maxLines={showMore ? undefined : 3}
                    >
                        {data.invoice_summary}
                    </MultilineText>
                </div>

                {showMore !== undefined && (
                    <Link quiet appearance="primary" onClick={toggleShowAll}>
                        {showMore ? 'Show Less' : 'Show More'}
                    </Link>
                )}
            </MessageCard>

            {/* <InvoiceTasks data={data} />*/}
        </Fragment>
    );
};
